import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { AppShell } from '@/components/layout/AppShell';
import AuthCallback from '@/pages/AuthCallback';

// Pages
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import AttendanceCheckinPage from '@/pages/AttendanceCheckinPage';
import AttendanceViewPage from '@/pages/AttendanceViewPage';
import BranchesPage from '@/pages/BranchesPage';
import EmployeesPage from '@/pages/EmployeesPage';
import LeavesPage from '@/pages/LeavesPage';
import HolidaysPage from '@/pages/HolidaysPage';
import SalaryPage from '@/pages/SalaryPage';
import SettingsPage from '@/pages/SettingsPage';
import ManualEntryPage from '@/pages/ManualEntryPage';
import WelcomePage from '@/pages/WelcomePage';
import TasksPage from '@/pages/TasksPage';
import LeadsPage from '@/pages/LeadsPage';
import LeadAnalyticsPage from '@/pages/LeadAnalyticsPage';
import OvertimePage from '@/pages/OvertimePage';

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-center">
          <div className="h-12 w-12 rounded-xl bg-primary/20 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  
  return <AppShell>{children}</AppShell>;
}

function AppRouter() {
  const location = useLocation();
  const { user, loading } = useAuth();

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions - useEffect runs AFTER first render, too late!
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-center">
          <div className="h-12 w-12 rounded-xl bg-primary/20 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute><DashboardPage /></ProtectedRoute>
      } />
      
      <Route path="/attendance/check" element={
        <ProtectedRoute><AttendanceCheckinPage /></ProtectedRoute>
      } />
      
      <Route path="/attendance" element={
        <ProtectedRoute><AttendanceViewPage /></ProtectedRoute>
      } />
      
      <Route path="/leaves" element={
        <ProtectedRoute><LeavesPage /></ProtectedRoute>
      } />

      <Route path="/tasks" element={
        <ProtectedRoute><TasksPage /></ProtectedRoute>
      } />

      <Route path="/leads" element={
        <ProtectedRoute><LeadsPage /></ProtectedRoute>
      } />

      <Route path="/leads/analytics" element={
        <ProtectedRoute><LeadAnalyticsPage /></ProtectedRoute>
      } />

      <Route path="/overtime" element={
        <ProtectedRoute><OvertimePage /></ProtectedRoute>
      } />
      
      <Route path="/employees" element={
        <ProtectedRoute roles={['super_user', 'manager']}><EmployeesPage /></ProtectedRoute>
      } />
      
      <Route path="/branches" element={
        <ProtectedRoute roles={['super_user']}><BranchesPage /></ProtectedRoute>
      } />
      
      <Route path="/holidays" element={
        <ProtectedRoute roles={['super_user', 'manager']}><HolidaysPage /></ProtectedRoute>
      } />
      
      <Route path="/salary" element={
        <ProtectedRoute roles={['super_user', 'manager']}><SalaryPage /></ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute roles={['super_user', 'manager']}><SettingsPage /></ProtectedRoute>
      } />
      
      <Route path="/admin/manual-entry" element={
        <ProtectedRoute roles={['super_user']}><ManualEntryPage /></ProtectedRoute>
      } />
      
      <Route path="*" element={<Navigate to={user ? '/dashboard' : '/login'} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouterWrapper />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppRouterWrapper() {
  const location = useLocation();
  
  // Public routes - no auth required
  if (location.pathname === '/welcome') {
    return <WelcomePage />;
  }
  
  // Also check for startsWith for query params edge cases
  if (location.pathname.startsWith('/welcome')) {
    return <WelcomePage />;
  }
  
  return <AppRouter />;
}

export default App;
